import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import api from '../lib/api';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);
  const [googleSsoLoading, setGoogleSsoLoading] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [branding, setBranding] = useState({
    default_logo_url: '/api/uploads/default_logo_fcf3c8c8.png',
    login_image_url: '/api/uploads/login_image_1773561515_4ade0a89.jpg'
  });
  const { login, loginWithToken } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const ssoHandledRef = useRef(false);

  // Fetch branding settings on load (public endpoint)
  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const response = await api.get('/settings/branding');
        if (response.data) {
          setBranding(prev => ({...prev, ...response.data}));
        }
      } catch (error) {
        console.error('Failed to fetch branding:', error);
      }
    };
    fetchBranding();
  }, []);

  // Handle SSO callback
  useEffect(() => {
    const ssoToken = searchParams.get('sso_token');
    const error = searchParams.get('error');
    
    // Prevent duplicate handling (React StrictMode calls useEffect twice)
    if (ssoHandledRef.current) return;
    
    if (ssoToken) {
      ssoHandledRef.current = true;
      // SSO login successful - use the token
      const handleSSOLogin = async () => {
        try {
          await loginWithToken(ssoToken);
          toast.success('Signed in successfully');
          navigate('/dashboard');
        } catch (err) {
          toast.error('Failed to complete sign-in. Please try again.');
          // Clear the URL params
          navigate('/login', { replace: true });
        }
      };
      handleSSOLogin();
    } else if (error) {
      ssoHandledRef.current = true;
      const errorMessages = {
        'microsoft_auth_failed': 'Microsoft authentication failed',
        'google_auth_failed': 'Google authentication failed',
        'no_auth_code': 'No authorization code received',
        'sso_not_configured': 'SSO is not configured',
        'token_exchange_failed': 'Failed to authenticate',
        'failed_to_get_user_info': 'Failed to get user information',
        'no_email_from_microsoft': 'No email address from Microsoft account',
        'no_email_from_google': 'No email address from Google account',
        'account_inactive': 'Your account is inactive. Contact your administrator.',
        'sso_error': 'An error occurred during sign-in',
      };
      toast.error(errorMessages[error] || 'Sign-in failed');
      // Clear the error from URL
      navigate('/login', { replace: true });
    }
  }, [searchParams, loginWithToken, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Login successful');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleMicrosoftSSO = async () => {
    setSsoLoading(true);
    try {
      const response = await api.get('/auth/microsoft/login');
      if (response.data.auth_url) {
        window.location.href = response.data.auth_url;
      } else {
        toast.error('Microsoft SSO is not configured');
        setSsoLoading(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Microsoft SSO is not available');
      setSsoLoading(false);
    }
  };

  const handleGoogleSSO = () => {
    setGoogleSsoLoading(true);
    // Use Emergent-managed Google Auth - redirects to dashboard after successful auth
    const redirectUrl = window.location.origin + '/dashboard';
    const authUrl = process.env.REACT_APP_AUTH_URL || 'https://auth.emergentagent.com';
    window.location.href = `${authUrl}/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (!resetEmail) {
      toast.error('Please enter your email address');
      return;
    }
    
    setResetLoading(true);
    try {
      const response = await api.post('/auth/password-reset', { email: resetEmail });
      if (response.data.email_sent) {
        toast.success('Password reset email sent! Check your inbox.');
        setResetDialogOpen(false);
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResetLoading(false);
    }
  };

  // Get logo URL with cache busting
  const getLogoUrl = () => {
    if (branding.default_logo_url) {
      return `${process.env.REACT_APP_BACKEND_URL}${branding.default_logo_url}?t=${Date.now()}`;
    }
    return null; // No fallback - will hide logo if not set
  };

  // Get login image URL with cache busting
  const getLoginImageUrl = () => {
    if (branding.login_image_url) {
      return `${process.env.REACT_APP_BACKEND_URL}${branding.login_image_url}?t=${Date.now()}`;
    }
    return null; // No fallback - will show solid color if not set
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Mobile Logo Header */}
      {getLogoUrl() && (
        <div className="lg:hidden bg-black p-4 flex items-center justify-center">
          <img 
            src={getLogoUrl()} 
            alt="Company Logo" 
            className="h-12"
          />
        </div>
      )}
      
      <div className="hidden lg:flex lg:w-1/2 relative bg-slate-900">
        {getLoginImageUrl() && (
          <img
            src={getLoginImageUrl()}
            alt="Office"
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative z-10 flex items-end p-12">
          <div className="text-white">
            {getLogoUrl() && (
              <img 
                src={getLogoUrl()} 
                alt="Company Logo" 
                className="h-20 mb-4"
              />
            )}
            <h1 className="text-4xl font-heading font-bold mb-2">Sales CRM</h1>
            <p className="text-lg opacity-90">Track deals, manage contacts, close more sales.</p>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-4 md:p-8">
        <div className="w-full max-w-md">
          <div className="mb-6 md:mb-8">
            <h2 className="text-2xl md:text-3xl font-heading font-bold mb-2">Welcome back</h2>
            <p className="text-sm md:text-base text-muted-foreground">Sign in to your account to continue</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" data-testid="login-email-label">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
                className="rounded-md"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" data-testid="login-password-label">Password</Label>
                <button
                  type="button"
                  onClick={() => { setResetDialogOpen(true); setResetEmail(email); }}
                  className="text-sm text-blue-600 hover:underline"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </button>
              </div>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
                className="rounded-md"
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-black hover:bg-zinc-900 text-white rounded-md"
              disabled={loading}
              data-testid="login-submit-button"
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">Or continue with</span>
            </div>
          </div>

          <div className="space-y-3">
            <Button
              type="button"
              variant="outline"
              className="w-full rounded-md"
              onClick={handleMicrosoftSSO}
              disabled={ssoLoading}
              data-testid="microsoft-sso-button"
            >
              {ssoLoading ? (
                'Redirecting...'
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
                    <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
                    <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
                    <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
                  </svg>
                  Sign in with Microsoft
                </>
              )}
            </Button>

            <Button
              type="button"
              variant="outline"
              className="w-full rounded-md"
              onClick={handleGoogleSSO}
              disabled={googleSsoLoading}
              data-testid="google-sso-button"
            >
              {googleSsoLoading ? (
                'Redirecting...'
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  Sign in with Google
                </>
              )}
            </Button>
          </div>

          <div className="mt-6 text-center text-sm">
            <span className="text-muted-foreground">Don't have an account? </span>
            <Link to="/register" className="text-black font-medium hover:underline" data-testid="register-link">
              Sign up
            </Link>
          </div>
        </div>
      </div>

      {/* Password Reset Dialog */}
      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <DialogContent className="rounded-lg">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
          </DialogHeader>
          <form onSubmit={handlePasswordReset} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="reset-email">Email Address</Label>
              <Input
                id="reset-email"
                type="email"
                placeholder="Enter your email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                required
                className="rounded-md"
                data-testid="reset-email-input"
              />
              <p className="text-sm text-muted-foreground">
                We'll send a temporary password to this email address.
              </p>
            </div>
            
            <div className="flex gap-2">
              <Button
                type="submit"
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
                disabled={resetLoading}
                data-testid="reset-password-submit"
              >
                {resetLoading ? 'Sending...' : 'Send Reset Email'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setResetDialogOpen(false)}
                className="rounded-md"
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Login;
